import Backdrop from '@mui/material/Backdrop';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import { RandomAvatar } from "react-random-avatars";
import PropTypes from 'prop-types';
import { useAuth } from 'react-oidc-context';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslations } from '../hooks/use-translations';

const ACCOUNT_NAME_REGEX = /^[A-Za-z0-9 _-]{3,64}$/;

export default function SetAccount({ accounts, setAccount, isLoading, canCreateAccount, createAccount, ...other }) {
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslations();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
  });
  
  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: 'rgba(0, 0, 0, 0)',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  }));

  const handleSignOut = () => {
    auth.signoutRedirect();
  };

  const handleOpenCreateDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (creating) {
      return;
    }
    setDialogOpen(false);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const normalizedName = formData.name.trim().replace(/\s+/g, ' ');
    if (!ACCOUNT_NAME_REGEX.test(normalizedName)) {
      return t('account.accountNameHelper');
    }

    const normalizedDomain = formData.domain.trim();
    if (normalizedDomain) {
      const invalidDomain = normalizedDomain
        .split(',')
        .map((domain) => domain.trim())
        .filter(Boolean)
        .find((domain) => !domain.startsWith('@'));

      if (invalidDomain) {
        return `${t('account.domainsMustStartWithAt')} (${invalidDomain}).`;
      }
    }

    return null;
  };

  const handleCreateAccount = async () => {
    if (!createAccount) {
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'error' });
      return;
    }

    const payload = {
      name: formData.name.trim().replace(/\s+/g, ' '),
      domain: formData.domain.trim() || null,
    };

    setCreating(true);
    try {
      await createAccount(payload);
      enqueueSnackbar(t('account.accountCreatedSuccessfully'), { variant: 'success' });
      setDialogOpen(false);
      setFormData({ name: '', domain: '' });
    } catch (error) {
      const message = error?.response?.data || t('account.failedToCreateAccount');
      enqueueSnackbar(typeof message === 'string' ? message : t('account.failedToCreateAccount'), { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  // Show loading spinner while fetching accounts
  if (isLoading) {
    return (
      <Backdrop open sx={{ background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(148,187,233,1) 100%)' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            {t('account.loadingAccounts')}
          </Typography>
        </Stack>
      </Backdrop>
    );
  }

  // If no accounts available, show no access message
  if (!accounts || accounts.length === 0) {
    return (
      <Backdrop open sx={{ background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(148,187,233,1) 100%)' }}>
        <Stack spacing={3} sx={{ maxWidth: 500, px: 3 }}>
          <Item>
            <Box
              component="img"
              src={`${process.env.REACT_APP_BASE_URL}assets/bosca_logo.png`}
              alt={t('account.cloudloqLogo')}
              style={{height: '3rem', margin:'1rem'}}
            />
          </Item>
          <Item>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('account.noAccessTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('account.noAccessDescription')}
            </Typography>
            {canCreateAccount && (
              <LoadingButton
                variant="contained"
                onClick={handleOpenCreateDialog}
                loading={creating}
                sx={{ mt: 1, mr: 1 }}
              >
                {t('account.createAccount')}
              </LoadingButton>
            )}
            <Button 
              variant="contained" 
              onClick={handleSignOut}
              sx={{ mt: 2 }}
            >
              {t('account.signOut')}
            </Button>
          </Item>
        </Stack>

        <Dialog open={dialogOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="sm">
          <DialogTitle>{t('account.createAccount')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                name="name"
                label={t('account.accountName')}
                value={formData.name}
                onChange={handleFieldChange}
                disabled={creating}
                helperText={t('account.accountNameHelper')}
                fullWidth
              />
              <TextField
                name="domain"
                label={t('account.allowedDomainsOptional')}
                value={formData.domain}
                onChange={handleFieldChange}
                disabled={creating}
                helperText={t('account.domainsHelper')}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog} disabled={creating}>
              {t('common.cancel')}
            </Button>
            <LoadingButton onClick={handleCreateAccount} loading={creating} variant="contained">
              {t('common.create')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Backdrop>
    );
  }

  // Normal account selection view
  return (
    <Backdrop open sx={{ background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(148,187,233,1) 100%)' }}>
      <Stack spacing={1}>
        <Item>
          <h2 data-testid="choose-account-to-login">{t('account.chooseAccountToLogin')}</h2>
        </Item>
        <Item>
          {accounts.map((account, i) => {     
            return (
              <MenuItem key={account.id} sx={{ py: 2, bgcolor: '', mb: 1, border: '1px solid grey', borderRadius: 1, }} onClick={() => setAccount(account)}>
                <ListItemIcon>
                  <Avatar sx={{ width: 28, height: 28 }}><RandomAvatar name={account.path} size={28} /></Avatar>
                </ListItemIcon>
                <ListItemText></ListItemText>
                <Typography variant="body2" color="text.secondary">
                  {account.name}
                </Typography>
              </MenuItem>
            ) 
          })}
        </Item>
        {canCreateAccount && (
          <Item>
            <Button variant="outlined" onClick={handleOpenCreateDialog}>
              {t('account.createAccount')}
            </Button>
          </Item>
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('account.createAccount')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              name="name"
              label={t('account.accountName')}
              value={formData.name}
              onChange={handleFieldChange}
              disabled={creating}
              helperText={t('account.accountNameHelper')}
              fullWidth
            />
            <TextField
              name="domain"
              label={t('account.allowedDomainsOptional')}
              value={formData.domain}
              onChange={handleFieldChange}
              disabled={creating}
              helperText={t('account.domainsHelper')}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={creating}>
            {t('common.cancel')}
          </Button>
          <LoadingButton onClick={handleCreateAccount} loading={creating} variant="contained">
            {t('common.create')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Backdrop>
  );
}

SetAccount.propTypes = {
  accounts: PropTypes.array,
  setAccount: PropTypes.func,
  isLoading: PropTypes.bool,
  canCreateAccount: PropTypes.bool,
  createAccount: PropTypes.func,
};

